import { saveAs } from 'file-saver';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';

import {
  BatchesGetAllResponse,
  BatchesGetDetailsResponse,
} from '../types/batches';
import {
  ContractsGetAllResponse,
  ContractsGetDetailsResponse,
  ContractImportTelepacResponse,
  ContractReassignNgpR26IlotsResponse,
  ContractRebuildIdentificationResponse,
  ContractReimportNgpR26TelepacsResponse,
  ContractReimportTelepacsResponse,
} from '../types/contracts';
import {
  ContractIlotFieldsParams,
  ContractIlotFieldsResponse,
} from '../types/contract-ilot-fields';
import {
  SignValidationBulkDto,
  SignValidationBulkResponse,
  ValidationFormDetailsResponse,
  ValidationFormsGetAllResponse,
  ValidationInfoResponse,
} from '../types/validation';

import { SearchParamsOption } from 'ky';

import { handleContractActionRequest } from '../utils/contract-action-request';

import {
  Filters,
  FORM_STATUS,
  formatFilters,
  handleRequest,
  MultisectActionDto,
  privateClient,
  publicClient,
} from '@data-access';

export const contractsApi = {
  getAll: async (filters: Filters) => {
    return handleRequest<ContractsGetAllResponse>(
      privateClient
        .get('contracts/', {
          searchParams: {
            ...formatFilters(filters),
          },
        })
        .json(),
    );
  },

  getDetails: async (id: number, campaignIds?: number[]) => {
    const searchParams: SearchParamsOption = {};
    if (campaignIds && campaignIds.length > 0) {
      // Ky's SearchParamsOption expects primitive values, so we serialize
      // the list of campaign IDs to a comma-separated string.
      searchParams.campaign = campaignIds.join(',');
    }
    return handleRequest<ContractsGetDetailsResponse>(
      privateClient
        .get(`contracts/${id}/`, {
          searchParams,
        })
        .json(),
    );
  },

  rebuildIdentification: async (contractId: number) => {
    return handleContractActionRequest(
      privateClient
        .post(`contracts/${contractId}/rebuild-identification/`)
        .json<ContractRebuildIdentificationResponse>(),
    );
  },

  reassignNgpR26Ilots: async (contractId: number) => {
    return handleContractActionRequest(
      privateClient
        .post(`contracts/${contractId}/reassign-ilots-ngp-r26/`)
        .json<ContractReassignNgpR26IlotsResponse>(),
    );
  },

  importTelepac: async (contractId: number, file: File) => {
    const body = new FormData();
    body.append('file', file);

    return handleContractActionRequest(
      privateClient
        .post(`contracts/${contractId}/import-telepac/`, {
          body,
        })
        .json<ContractImportTelepacResponse>(),
    );
  },

  reimportTelepacs: async (contractId: number) => {
    return handleContractActionRequest(
      privateClient
        .post(`contracts/${contractId}/reimport-telepacs/`)
        .json<ContractReimportTelepacsResponse>(),
    );
  },

  reimportNgpR26Telepacs: async (contractId: number) => {
    return handleContractActionRequest(
      privateClient
        .post(`contracts/${contractId}/reimport-telepacs-ngp-r26/`)
        .json<ContractReimportNgpR26TelepacsResponse>(),
    );
  },

  getIlotFields: async (
    contractId: number,
    { campaignIds, speciesIds }: ContractIlotFieldsParams = {},
  ) => {
    const searchParams: SearchParamsOption = {};
    if (campaignIds && campaignIds.length > 0) {
      searchParams.campaign = campaignIds.join(',');
    }
    if (speciesIds && speciesIds.length > 0) {
      searchParams.species = speciesIds.join(',');
    }
    const response = await handleRequest<ContractIlotFieldsResponse>(
      privateClient
        .get(`contracts/${contractId}/ilot-fields/`, {
          searchParams,
        })
        .json(),
    );

    return {
      ...response,
      layers: response.layers.map((layer) => ({
        ...layer,
        fields: {
          ...layer.fields,
          features: layer.fields.features.map((feature) => ({
            ...feature,
            properties: {
              ...feature.properties,
              id: feature.id as number,
            },
          })),
        },
      })),
    };
  },

  batches: {
    getAll: async ({ contractId, ...filters }: BatchesGetAllParams) => {
      return handleRequest<BatchesGetAllResponse>(
        privateClient.get(`contracts/${contractId}/lots/`).json(),
      );
    },

    getDetails: async (id: number) => {
      const response = await handleRequest<BatchesGetDetailsResponse>(
        privateClient.get(`lots/${id}/`).json(),
      );

      return {
        ...response,
        lot_fields: {
          ...response.lot_fields,
          features: response.lot_fields.features.map((feature) => {
            return {
              ...feature,
              properties: {
                ...feature.properties,
                id: feature.id as number,
              },
            };
          }),
        },
      };
    },

    importFile: async ({
      file,
      contractId,
      connectivityId,
      campaign,
    }: BatchesImportFileParams) => {
      const data = new FormData();
      data.append('contract', contractId.toString());
      data.append('campaign', campaign.toString());
      data.append('file', file);
      data.append('action', 'import');

      return handleRequest<{
        status: string;
        async_computation_request_id: number;
      }>(
        privateClient
          .post(`connectivity/${connectivityId}/`, {
            body: data,
          })
          .json(),
      );
    },

    exportFile: async ({
      contractId,
      connectivityId,
      campaign,
    }: Omit<BatchesImportFileParams, 'file'>) => {
      const data = new FormData();
      data.append('contract', contractId.toString());
      data.append('campaign', campaign.toString());
      data.append('action', 'export');

      const res = await handleRequest(
        privateClient.post(`connectivity/${connectivityId}/`, {
          body: data,
        }),
      );

      // Extract filename from header
      const filename =
        res.headers
          .get('content-disposition')
          ?.split(';')
          .find((n) => n.includes('filename='))
          ?.replace('filename=', '')
          .trim() || `export_${contractId}_${campaign}.zip`;

      const blob = await res.blob();

      saveAs(blob, filename);

      return res;
    },

    confirmBatch: async (id: number) => {
      return handleRequest(
        privateClient
          .patch(`lots/${id}/`, {
            json: {
              is_validated: true,
            },
          })
          .json(),
      );
    },
  },

  consent: {
    getConsentInfo: async (token: string) => {
      return handleRequest<{
        user: string;
        email: string;
        organization: string;
        file: string;
        'is signed': boolean;
      }>(
        publicClient
          .get(`consentform/`, {
            searchParams: {
              token,
            },
          })
          .json(),
      );
    },
    signConsent: async (token: string) => {
      return handleRequest(
        publicClient
          .post(`consentform/`, {
            searchParams: {
              token,
            },
          })
          .json(),
      );
    },
  },
  validation: {
    getValidationInfo: async (token: string) => {
      return handleRequest<ValidationInfoResponse>(
        publicClient
          .get(`validationform/`, {
            searchParams: {
              token,
            },
          })
          .json(),
      );
    },
    signConsent: async (token: string) => {
      return handleRequest(
        publicClient
          .post(`validationform/`, {
            searchParams: {
              token,
            },
          })
          .json(),
      );
    },
    /** Farmer validation — replaces deprecated POST fieldanswers/:id/ per parcel. */
    signValidationBulk: async ({ token, answers }: SignValidationBulkDto) => {
      return handleRequest<SignValidationBulkResponse>(
        publicClient
          .post(`fieldanswers/bulk/`, {
            searchParams: {
              token,
            },
            json: { answers },
          })
          .json(),
      );
    },
  },

  validationForms: {
    getAll: async (filters: Filters) => {
      return handleRequest<ValidationFormsGetAllResponse>(
        privateClient
          .get('validationforms/', {
            searchParams: {
              ...formatFilters(filters),
            },
          })
          .json(),
      );
    },
    getDetails: async (id: number) => {
      return handleRequest<ValidationFormDetailsResponse>(
        privateClient.get(`validationforms/${id}/`).json(),
      );
    },
    sendReminderMail: async (data: MultisectActionDto) => {
      return handleRequest(
        privateClient
          .post('validationforms/send_reminder_mail/', {
            json: data,
          })
          .json(),
      );
    },
    prepExport: async (data: {
      filters: MRT_ColumnFiltersState;
      sorting: MRT_SortingState;
    }) => {
      const sort = data.sorting[0]
        ? `${data.sorting[0].desc ? '-' : ''}${data.sorting[0].id.replace(/\./g, '__')}`
        : undefined;
      return handleRequest<ValidationFormsPrepExportResponse>(
        privateClient
          .post('validationforms/prep_export/', {
            json: {
              selectedFilters: data.filters,
              ...(sort ? { sort } : {}),
            },
          })
          .json(),
      );
    },
    downloadExport: async (opts: { id: number }) => {
      const res = await handleRequest(
        privateClient.post('validationforms/export/', {
          json: {
            instance_id: opts.id,
          },
        }),
      );

      const filename =
        res.headers
          .get('content-disposition')
          ?.split(';')
          .find((part) => part.includes('filename='))
          ?.replace('filename=', '')
          .trim() || 'suivi-agriculteurs.xlsx';

      const blob = await res.blob();
      saveAs(blob, filename);
    },
  },
};

export type ValidationFormsPrepExportResponse = {
  id: number;
  status: FORM_STATUS;
  instance_id: number;
};

export type BatchesGetAllParams = {
  contractId: number;
};

export type BatchesImportFileParams = {
  contractId: number;
  connectivityId: number;
  campaign: number;
  file: File;
};

